/**
 * Documentos legales accesibles in-app (v1.4.7). LFPDPPP espera que el Aviso de
 * Privacidad sea consultable en cualquier momento, no solo al alta. Esta sección
 * (Configuración) deja al admin:
 *   - Revisitar el Aviso de Privacidad + TyC VIGENTES (link al doc + PDF).
 *   - Ver el historial de versiones de cada documento (del frontmatter de
 *     docs/legal/<slug>.md — hoy solo existe la vigente; ver nota).
 *   - Ver QUÉ versión aceptó su organización y cuándo (de legal_acceptances, V24,
 *     leído vía GET /legal/acceptances → fn_get_legal_acceptances, V28).
 *
 * Reusa las páginas estáticas /legal/<slug> (que ya renderizan el doc + botón de
 * descargar PDF), así que "ver / descargar PDF" abre esa página canónica.
 */
import { useEffect, useMemo, useState } from 'react';
import { FileText, ExternalLink, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import {
  LEGAL_DOCS, legalPath, legalVersionPath, PRIOR_LEGAL_VERSIONS, type LegalSlug,
} from './legalConfig';
import { parseFrontmatter, formatDateDMY, type LegalFrontmatter } from './frontmatter';

interface Acceptance {
  documentType: string;
  documentSlug: string | null;
  documentLabel: string;
  documentVersion: string;
  acceptedAt: string;
  acceptedByEmail: string | null;
  /** Legales v3.1 — TRUE si la aceptación es de un Administrador (vinculante). */
  isAdmin?: boolean;
}

const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export function LegalSettings() {
  const [acceptances, setAcceptances] = useState<Acceptance[]>([]);
  const [docs, setDocs] = useState<Record<string, LegalFrontmatter>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = (await api('/legal/acceptances')) as Acceptance[];
        if (!cancelled) setAcceptances(Array.isArray(a) ? a : []);
      } catch { if (!cancelled) setAcceptances([]); }

      const entries = await Promise.all(
        LEGAL_DOCS.map(async (d) => {
          try {
            const res = await fetch(`${legalPath(d.slug)}.md`, { headers: { Accept: 'text/markdown' } });
            if (!res.ok) return [d.slug, {}] as const;
            const text = await res.text();
            if (/^\s*<!doctype html/i.test(text) || text.includes('<div id="root">')) return [d.slug, {}] as const;
            return [d.slug, parseFrontmatter(text).data] as const;
          } catch { return [d.slug, {}] as const; }
        }),
      );
      if (!cancelled) { setDocs(Object.fromEntries(entries)); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const acceptanceBySlug = useMemo(() => {
    const m: Record<string, Acceptance> = {};
    for (const a of acceptances) if (a.documentSlug) m[a.documentSlug] = a;
    return m;
  }, [acceptances]);

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="flex items-center gap-2 font-serif text-lg tracking-tight text-slate-900">
          <ShieldCheck className="h-4 w-4 text-slate-400" /> Documentos legales
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Consulta el Aviso de Privacidad y los Términos vigentes, su historial de versiones y qué versión aceptó tu organización.
        </p>
      </div>

      {loading ? (
        <p className="px-6 py-6 text-sm text-slate-500 md:px-8">Cargando…</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {LEGAL_DOCS.map((d) => {
            const fm = docs[d.slug] ?? {};
            const accepted = acceptanceBySlug[d.slug as LegalSlug];
            const vigente = fm.version || 's/versión';
            return (
              <div key={d.slug} className="px-6 py-5 md:px-8">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 font-medium text-slate-800">
                      <FileText className="h-4 w-4 text-slate-400" /> {d.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Vigente: <strong>{vigente}</strong>
                      {fm.updated && <> · actualizado {formatDateDMY(fm.updated)}</>}
                      {(fm.status || '').toLowerCase() === 'draft' && (
                        <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">borrador</span>
                      )}
                    </p>
                  </div>
                  <a
                    href={legalPath(d.slug)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Ver / descargar PDF
                  </a>
                </div>

                {/* Aceptación del tenant (solo aplica a los docs que se aceptan al alta) */}
                {accepted ? (
                  <p className="mt-2 rounded-lg bg-emerald-50/60 px-3 py-2 text-[11px] text-emerald-800 ring-1 ring-inset ring-emerald-600/10">
                    Tu organización aceptó la versión <strong>{accepted.documentVersion}</strong> el {fmtDateTime(accepted.acceptedAt)}
                    {accepted.acceptedByEmail && <> · por {accepted.acceptedByEmail}</>}
                    {accepted.isAdmin && (
                      <span className="ml-1 rounded bg-emerald-600/10 px-1.5 py-0.5 font-medium text-emerald-800">Administrador (vinculante)</span>
                    )}.
                  </p>
                ) : (
                  <p className="mt-2 text-[11px] text-slate-400">Sin registro de aceptación para este documento.</p>
                )}

                {/* Historial de versiones (frontmatter). Hoy solo existe la vigente. */}
                <div className="mt-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Versiones</p>
                  <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600">
                    <li className="flex items-center gap-2">
                      <span className="rounded-full bg-slate-900 px-1.5 py-0.5 text-[9px] font-medium text-white">vigente</span>
                      {vigente}{fm.updated && <span className="text-slate-400"> · {formatDateDMY(fm.updated)}</span>}
                      <a href={legalPath(d.slug)} target="_blank" rel="noopener noreferrer" className="ml-1 text-slate-400 underline hover:text-slate-700">ver</a>
                    </li>
                    {/* Legales v3.1 — versiones anteriores accesibles por URL (§16). */}
                    {PRIOR_LEGAL_VERSIONS.map((v) => (
                      <li key={v} className="flex items-center gap-2 text-slate-500">
                        <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">anterior</span>
                        v{v}
                        <a href={legalVersionPath(v, d.slug)} target="_blank" rel="noopener noreferrer" className="ml-1 text-slate-400 underline hover:text-slate-700">ver</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
